import { useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  RiDatabase2Line,
  RiCheckLine,
  RiDownloadLine,
  RiFileCopyLine,
} from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  Flex,
  InfraCodeBlock,
  InfraDivider,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagValue,
  InfraTagRow,
  Text,
} from '../ui/InfraPrimitives';

export const OracleCDCInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const templateType = annotations['infrastructure.backstage.io/type'] || '';
  const oracleHost = annotations['oracle.infrastructure/host'];
  const oraclePort = annotations['oracle.infrastructure/port'];
  // Multi-database hosts (aws-oracle-cdc-source with oracle_instance_count > 1):
  // one container per listener port, all sharing the same service name and PDB.
  const oraclePorts = annotations['oracle.infrastructure/ports'];
  const oracleSid = annotations['oracle.infrastructure/sid'];
  const oraclePdb = annotations['oracle.infrastructure/pdb'];
  // Legacy single-database entries wrote the PDB under /database.
  const oracleDatabase = annotations['oracle.infrastructure/database'];
  const oracleUser = annotations['oracle.infrastructure/user'];
  const oraclePassword = annotations['oracle.infrastructure/password'];
  const oracleVersion = annotations['oracle.infrastructure/version'];
  const cdcEnabled = annotations['oracle.infrastructure/cdc-enabled'];
  const archiveLogMode = annotations['oracle.infrastructure/archive-log-mode'];
  const sshCommand = annotations['oracle.infrastructure/ssh-command'];
  const sshPrivateKeyEncoded =
    annotations['oracle.infrastructure/ssh-private-key'];

  if (templateType !== 'aws-oracle-cdc-source' || !oracleHost) return null;

  const sshPrivateKey = useMemo(() => {
    if (!sshPrivateKeyEncoded) return '';
    try {
      return window.atob(sshPrivateKeyEncoded);
    } catch (e) {
      console.error('Failed to decode SSH key:', e);
      return '';
    }
  }, [sshPrivateKeyEncoded]);

  const downloadSSHKey = () => {
    const element = document.createElement('a');
    const file = new Blob([sshPrivateKey], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'oracle_key.pem';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const host = oracleHost;
  const ports = (oraclePorts || oraclePort || '1521')
    .split(',')
    .map(p => p.trim())
    .filter(Boolean);
  // CDB service name goes in connection.database; the PDB is a Debezium property.
  const service = oracleSid || oracleDatabase || 'XE';
  const pdb = oraclePdb || (oracleSid ? '' : oracleDatabase || '');
  const user = oracleUser || 'c##rdiuser';
  const password = oraclePassword || 'RedisCDC2024!';

  // Must match source_tags in ps-backstage-infra terraform/stacks/aws-oracle-cdc-source/main.tf
  // and the seed data each database is tagged with (SMS.SEC_SESSION rows carry the tag).
  const sourceTags = ['QA03', 'RSQA', 'SRC3', 'SRC4'];
  const sourceName = (i: number) =>
    `oracle-${(sourceTags[i] || `src${i + 1}`).toLowerCase()}`;

  const rdiSources = ports
    .map(
      (p, i) => `  ${sourceName(i)}:
    type: cdc
    connection:
      type: oracle
      host: ${host}
      port: ${p}
      database: ${service}
      user: ${user}
      password: ${password}
    schemas: [SMS]
    tables:
      SMS.SEC_SESSION: {}
      SMS.RDI_HEARTBEAT: {}
    advanced:
      source:${pdb ? `\n        database.pdb.name: ${pdb}` : ''}
        decimal.handling.mode: string
        log.mining.strategy: online_catalog
        snapshot.mode: initial
        heartbeat.interval.ms: 10000
        heartbeat.action.query: UPDATE SMS.RDI_HEARTBEAT SET TS = SYSTIMESTAMP WHERE ID = 1`,
    )
    .join('\n');

  const rdiConfig = `# ${ports.length} Oracle database${
    ports.length === 1 ? '' : 's'
  } on this host, one RDI source each
sources:
${rdiSources}`;

  return (
    <InfoCard
      title="Oracle CDC Source Information"
      icon={<RiDatabase2Line size={20} />}
    >
      <InfraStack>
        <InfraSection title="🔴 Oracle Configuration">
          {oracleVersion && (
            <InfraField
              label="Version:"
              value={<InfraTagValue>{`Oracle ${oracleVersion}`}</InfraTagValue>}
              mono={false}
            />
          )}
          {cdcEnabled === 'true' && (
            <InfraField
              label="CDC Status:"
              value={
                <InfraTagRow>
                  <InfraTag>
                    <RiCheckLine size={14} style={{ marginRight: 4 }} />
                    Enabled
                  </InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {archiveLogMode && (
            <InfraField
              label="Archive Log Mode:"
              value={<InfraTagValue>{archiveLogMode}</InfraTagValue>}
              mono={false}
            />
          )}
        </InfraSection>

        <InfraSection title="🔌 Connection Information">
          <CopyableValue label="Host:" value={host} />
          <InfraField label="Databases:" value={String(ports.length)} />
          <InfraField
            label={ports.length === 1 ? 'Port:' : 'Ports:'}
            value={ports.join(', ')}
          />
          <InfraField label="Service (SID):" value={service} />
          {pdb && <InfraField label="PDB:" value={pdb} />}
          <InfraField label="User:" value={user} />
          {oraclePassword && (
            <CopyableValue label="Password:" value={oraclePassword} />
          )}
        </InfraSection>

        <InfraDivider />
        <InfraSection title="🚀 RDI Configuration">
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock variant="light">{rdiConfig}</InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={rdiConfig} />
            </div>
          </div>
        </InfraSection>

        {sshPrivateKey && (
          <>
            <InfraDivider />
            <InfraSection title="🔐 SSH Access">
              {sshCommand && (
                <>
                  <InfraField label="SSH Command:" value="" mono={false} />
                  <div style={{ position: 'relative' }}>
                    <InfraCodeBlock variant="light">
                      {sshCommand}
                    </InfraCodeBlock>
                    <div style={{ position: 'absolute', top: 4, right: 4 }}>
                      <CopyButton text={sshCommand} />
                    </div>
                  </div>
                </>
              )}
              <InfraField label="SSH Private Key:" value="" mono={false} />
              <Flex gap="2">
                <Button variant="primary" size="small" onPress={downloadSSHKey}>
                  <RiDownloadLine size={16} />
                  Download Key
                </Button>
                <Button
                  variant="secondary"
                  size="small"
                  onPress={() => navigator.clipboard.writeText(sshPrivateKey)}
                >
                  <RiFileCopyLine size={16} />
                  Copy Key
                </Button>
              </Flex>
              <Text
                variant="body-small"
                style={{ marginTop: '0.5rem', display: 'block' }}
              >
                💡 Save as oracle_key.pem and run: chmod 400 oracle_key.pem
              </Text>
            </InfraSection>
          </>
        )}
      </InfraStack>
    </InfoCard>
  );
};
